import {
  getOncePosition, getLocation, defaultLocation,
} from '@m2h/common/utils/location';

// Web 端 SLLocation 权限模型：
// - Native/KSI 契约（locationAuthorizationStatus / locationServicesEnabled）是同步调用；
// - Web 由 bootstrap（mainPC）在业务渲染前调用一次 initializeAuthorizationStatus()，
//   single-flight 完成权限快照，此后所有业务同步读取同一快照；
// - 权限快照只能由 Permissions API 或原生 navigator.geolocation 探测更新；
//   Mars/network/cache 定位链（getOncePosition）的成功/失败不代表浏览器权限状态，禁止写入快照。
type InternalAuthorizationState =
  | 'granted' // 明确已授权
  | 'denied' // 明确已拒绝（Permissions API 或 getCurrentPosition code 1）
  | 'prompt' // Permissions API 明确报告未选择
  | 'unsupported' // 浏览器没有 navigator.geolocation
  | 'indeterminate'; // 有 geolocation 但状态无法判定：探测超时 / code 2、3 / 查询失败
type AuthorizationSnapshotSource =
  | 'permissions-api' | 'geolocation-probe' | 'unsupported' | 'fallback';

interface AuthorizationSnapshot {
  initialized: boolean;
  servicesEnabled: boolean;
  authorizationStatus: number;
  internalState: InternalAuthorizationState;
  source: AuthorizationSnapshotSource;
}

// AuthorizationSnapshot.internalState/source 仅供 Web 桥层、覆盖层和测试诊断使用，
// 不扩散给根业务——根业务只判断 locationAuthorizationStatus() > 0。
type AuthorizationChangeListener = (
  status: number,
  state: InternalAuthorizationState
) => void;

const authorizationChangeListeners = new Set<AuthorizationChangeListener>();

const locationServicesEnabled = () => {
  if (typeof navigator === 'undefined') return false;
  try {
    // 明确缺少 geolocation 才是 unsupported；Harmony/旧 WebView getter 抛错属于未知异常。
    const geolocation = navigator.geolocation;
    return geolocation !== undefined && geolocation !== null;
  } catch (error) {
    console.log('SLLocation geolocation capability is unavailable', error);
    return true;
  }
};

// 初始快照：未初始化且没有明确拒绝证据。Web/Harmony 兼容策略为 fail-open：
// 只有明确 denied 才按未授权处理；prompt、浏览器异常、超时等状态默认有权限。
const initialServicesEnabled = locationServicesEnabled();
let authorizationSnapshot: AuthorizationSnapshot = {
  initialized: false,
  servicesEnabled: initialServicesEnabled,
  authorizationStatus: initialServicesEnabled ? 1 : 0,
  internalState: 'indeterminate',
  source: 'fallback',
};

let permissionStatus: PermissionStatus | null = null;
let permissionStatusListenerMode: 'event-listener' | 'onchange' | null = null;
let previousPermissionStatusOnChange: PermissionStatus['onchange'] = null;
let initializePromise: Promise<void> | null = null;
let permissionObservationRevision = 0;
let directRequestRevision = 0;
let requestLocationPermissionPromise: Promise<boolean> | null = null;

// prompt 只表示尚未作出选择，并不能证明无权限；仅 denied 是明确拒绝。
const isAuthorizedState = (internalState: InternalAuthorizationState) => (
  internalState !== 'denied'
);

const notifyAuthorizationChange = () => {
  authorizationChangeListeners.forEach((listener) => {
    try {
      listener(authorizationSnapshot.authorizationStatus, authorizationSnapshot.internalState);
    } catch (error) {
      console.error('SLLocation authorization listener error', error);
    }
  });
};

// 只有原生浏览器权限探测才允许写快照；Mars/network/cache 定位链不得调用。
const commitSnapshot = (
  internalState: InternalAuthorizationState,
  source: AuthorizationSnapshotSource
) => {
  const servicesEnabled = locationServicesEnabled();
  const nextSnapshot: AuthorizationSnapshot = {
    initialized: true,
    servicesEnabled,
    authorizationStatus: isAuthorizedState(internalState) && servicesEnabled ? 1 : 0,
    internalState,
    source,
  };
  // 数值或内部状态任一变化都通知订阅者：V2 等覆盖层依赖 internalState 区分
  // prompt（可引导申请）与 denied（已拒绝），数值相同不代表语义相同。
  const snapshotChanged = nextSnapshot.authorizationStatus !== authorizationSnapshot.authorizationStatus
    || nextSnapshot.internalState !== authorizationSnapshot.internalState;
  authorizationSnapshot = nextSnapshot;
  if (snapshotChanged) {
    notifyAuthorizationChange();
  }
};

interface PermissionQueryResult {
  status: PermissionStatus;
  state: PermissionState | null;
}

interface PermissionObservation {
  revision: number;
  directRequestRevision: number;
}

const beginPermissionObservation = (): PermissionObservation => ({
  revision: ++permissionObservationRevision,
  directRequestRevision,
});

const isCurrentPermissionObservation = (observation: PermissionObservation) => (
  observation.revision === permissionObservationRevision
  && observation.directRequestRevision === directRequestRevision
);

const invalidatePermissionObservations = () => {
  permissionObservationRevision += 1;
};

const normalizePermissionState = (state: unknown): PermissionState | null => {
  if (state === 'granted' || state === 'denied' || state === 'prompt') {
    return state;
  }
  return null;
};

const handlePermissionStatusChange = () => {
  let state: PermissionState | null = null;
  try {
    state = normalizePermissionState(permissionStatus?.state);
  } catch (error) {
    console.log('SLLocation permission change state is unavailable', error);
  }
  // change 事件代表权限事实已变化；非标准/未知值按 indeterminate fail-open。
  directRequestRevision += 1;
  invalidatePermissionObservations();
  commitSnapshot(state ?? 'indeterminate', state ? 'permissions-api' : 'fallback');
};

const handlePermissionStatusOnChange = (event: Event) => {
  const previousOnChange = previousPermissionStatusOnChange;
  if (typeof previousOnChange === 'function') {
    try {
      previousOnChange.call(permissionStatus, event);
    } catch (error) {
      console.error('SLLocation previous permission onchange error', error);
    }
  }
  handlePermissionStatusChange();
};

const removePermissionStatusListener = () => {
  try {
    if (permissionStatusListenerMode === 'event-listener') {
      permissionStatus?.removeEventListener?.('change', handlePermissionStatusChange);
    } else if (
      permissionStatusListenerMode === 'onchange'
      && permissionStatus?.onchange === handlePermissionStatusOnChange
    ) {
      permissionStatus.onchange = previousPermissionStatusOnChange;
    }
  } catch (error) {
    // 非标准 PermissionStatus 的监听清理失败不能打断权限刷新或页面卸载。
    console.log('SLLocation permission listener cleanup is unavailable', error);
  }
  permissionStatusListenerMode = null;
  previousPermissionStatusOnChange = null;
};

const addPermissionStatusListener = () => {
  if (
    authorizationChangeListeners.size === 0
    || permissionStatusListenerMode
    || !permissionStatus
  ) {
    return;
  }

  try {
    if (typeof permissionStatus.addEventListener === 'function') {
      permissionStatus.addEventListener('change', handlePermissionStatusChange);
      permissionStatusListenerMode = 'event-listener';
      return;
    }
  } catch (error) {
    console.log('SLLocation permission addEventListener is unavailable', error);
  }

  // 部分 Harmony/旧 WebView 只实现 PermissionStatus.onchange。
  try {
    if ('onchange' in permissionStatus) {
      previousPermissionStatusOnChange = permissionStatus.onchange;
      permissionStatus.onchange = handlePermissionStatusOnChange;
      permissionStatusListenerMode = 'onchange';
    }
  } catch (error) {
    console.log('SLLocation permission onchange is unavailable', error);
  }
};

const adoptPermissionStatus = (nextPermissionStatus: PermissionStatus) => {
  if (permissionStatus !== nextPermissionStatus) {
    removePermissionStatusListener();
    permissionStatus = nextPermissionStatus;
  }
  // 无论订阅发生在 query 之前还是之后，都补挂 change/onchange 监听。
  addPermissionStatusListener();
};

const PERMISSIONS_QUERY_TIMEOUT_MS = 1200;

// 仅通过 Permissions API 查询权限状态；查询有硬超时，且直到调用方确认结果仍为最新前不改全局状态。
const queryPermissionsApi = (): Promise<PermissionQueryResult | null> => {
  let queryPromise: Promise<PermissionStatus>;
  try {
    // Harmony/旧 WebView 可能在读取 navigator.permissions 或 query 属性时同步抛错。
    const permissions = typeof navigator !== 'undefined' ? navigator.permissions : undefined;
    const query = permissions?.query;
    if (typeof query !== 'function') return Promise.resolve(null);
    queryPromise = query.call(permissions, { name: 'geolocation' });
  } catch (error) {
    console.log('SLLocation permission query is unavailable', error);
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const settle = (result: PermissionQueryResult | null) => {
      if (settled) return;
      settled = true;
      if (watchdog !== undefined) {
        clearTimeout(watchdog);
      }
      resolve(result);
    };

    watchdog = setTimeout(() => settle(null), PERMISSIONS_QUERY_TIMEOUT_MS);
    Promise.resolve(queryPromise)
      .then((nextPermissionStatus) => {
        if (
          !nextPermissionStatus
          || (typeof nextPermissionStatus !== 'object' && typeof nextPermissionStatus !== 'function')
        ) {
          settle(null);
          return;
        }
        try {
          settle({
            status: nextPermissionStatus,
            state: normalizePermissionState(nextPermissionStatus.state),
          });
        } catch (error) {
          console.log('SLLocation permission state is unavailable', error);
          settle(null);
        }
      })
      .catch((error) => {
        // Safari/Harmony 等环境可能 reject；无明确 denied 结论，交给 fail-open/probe 兜底。
        console.log('SLLocation permission query is unavailable', error);
        settle(null);
      });
  });
};

const isPermissionDeniedError = (error: any) => {
  // 仅 code 1 (PERMISSION_DENIED) 能证明权限被拒绝；
  // code 2 (POSITION_UNAVAILABLE) / code 3 (TIMEOUT) 是定位失败，不是权限结论。
  try {
    const rawCode = error?.code ?? error?.errorCode ?? error?.error?.code;
    return Number(rawCode) === 1;
  } catch {
    // Harmony/非标准错误对象可能在 getter 或 ToNumber 转换时抛错；不得据此推断 denied。
    return false;
  }
};

const PROBE_TIMEOUT_MS = 8000;

// 启动阶段执行一次原生 getCurrentPosition 探测。
// 已授权用户通常不再次弹框；未选择用户可能在启动时看到浏览器授权框（方案既定取舍）。
const probeWithNativeGeolocation = (): Promise<'granted' | 'denied' | 'indeterminate'> =>
  new Promise((resolve) => {
    let settled = false;
    const settle = (state: 'granted' | 'denied' | 'indeterminate') => {
      if (settled) return;
      settled = true;
      resolve(state);
    };
    // settle guard：超时后晚到的 callback 不再产生结论（由 settled 标记挡住）。
    const watchdog = setTimeout(() => settle('indeterminate'), PROBE_TIMEOUT_MS);
    let geolocation: Geolocation;
    try {
      geolocation = navigator.geolocation;
    } catch (error) {
      // capability getter 异常不是浏览器拒绝的权限事实。
      clearTimeout(watchdog);
      settle('indeterminate');
      return;
    }
    const settleProbeError = (error: any) => {
      let state: 'denied' | 'indeterminate' = 'indeterminate';
      try {
        state = isPermissionDeniedError(error) ? 'denied' : 'indeterminate';
      } catch {
        // 保持 indeterminate：异步 error callback 不得将异常逸出或留下 pending Promise。
      }
      clearTimeout(watchdog);
      settle(state);
    };
    try {
      geolocation.getCurrentPosition(
        () => {
          clearTimeout(watchdog);
          settle('granted');
        },
        (error) => {
          settleProbeError(error);
        },
        {
          enableHighAccuracy: false,
          maximumAge: 0,
          timeout: PROBE_TIMEOUT_MS - 1000,
        }
      );
    } catch (error) {
      settleProbeError(error);
    }
  });

const detectAuthorization = async (observation: PermissionObservation): Promise<void> => {
  if (!locationServicesEnabled()) {
    if (isCurrentPermissionObservation(observation)) {
      commitSnapshot('unsupported', 'unsupported');
    }
    return;
  }

  const permissionResult = await queryPermissionsApi();
  if (!isCurrentPermissionObservation(observation)) return;

  if (permissionResult) {
    adoptPermissionStatus(permissionResult.status);
  }
  const permissionState = permissionResult?.state ?? null;
  if (permissionState === 'granted' || permissionState === 'denied') {
    commitSnapshot(permissionState, 'permissions-api');
    return;
  }

  // prompt 只是 Permissions API 的状态查询结果，不会唤起浏览器授权框；
  // 必须继续调用 getCurrentPosition 才能在初始化阶段发起一次真实权限探测。
  // 浏览器若因位置不可用/超时没有形成权限结论，统一落 indeterminate 并 fail-open，
  // 避免 Harmony 等兼容性异常被误判为无权限。
  const probeState = await probeWithNativeGeolocation();
  if (isCurrentPermissionObservation(observation)) {
    commitSnapshot(probeState, 'geolocation-probe');
  }
};

// 唯一的 bootstrap 异步入口：single-flight、固定超时、永不 reject（异常时落 fallback 快照）。
// 只允许 Web bootstrap（mainPC）调用，不下沉到业务页面。
function initializeAuthorizationStatus(): Promise<void> {
  if (initializePromise) {
    return initializePromise;
  }

  const observation = beginPermissionObservation();
  initializePromise = detectAuthorization(observation)
    .catch((error) => {
      // 初始化异常不能白屏：落 fallback 快照放行业务。
      console.log('SLLocation initializeAuthorizationStatus error', error);
      if (isCurrentPermissionObservation(observation)) {
        commitSnapshot('indeterminate', 'fallback');
      }
    });

  return initializePromise;
}

const refreshAuthorizationStatus = async () => {
  if (!locationServicesEnabled()) {
    invalidatePermissionObservations();
    commitSnapshot('unsupported', 'unsupported');
    return authorizationSnapshot.authorizationStatus;
  }

  // 用户手势定位是更直接的权限事实；其进行中不再启动被动 query 与之竞争。
  if (requestLocationPermissionPromise) {
    await requestLocationPermissionPromise;
    return authorizationSnapshot.authorizationStatus;
  }

  const observation = beginPermissionObservation();
  let permissionResult: PermissionQueryResult | null = null;
  try {
    permissionResult = await queryPermissionsApi();
  } catch (error) {
    // 防御非标准 Permissions API/thenable 的未知抛错；refresh 对业务永不 reject。
    console.log('SLLocation permission refresh is unavailable', error);
  }
  if (!isCurrentPermissionObservation(observation)) {
    return authorizationSnapshot.authorizationStatus;
  }

  if (permissionResult) {
    try {
      adoptPermissionStatus(permissionResult.status);
    } catch (error) {
      // 非标准 PermissionStatus 监听能力失败不影响已规范化的权限事实。
      console.log('SLLocation permission status is unavailable', error);
    }
  }
  const permissionState = permissionResult?.state ?? 'indeterminate';
  commitSnapshot(
    permissionState,
    permissionResult?.state ? 'permissions-api' : 'fallback'
  );
  return authorizationSnapshot.authorizationStatus;
};

const requestLocationPermission = (): Promise<boolean> => {
  if (requestLocationPermissionPromise) {
    return requestLocationPermissionPromise;
  }

  directRequestRevision += 1;
  invalidatePermissionObservations();
  const requestRevision = directRequestRevision;
  if (!locationServicesEnabled()) {
    commitSnapshot('unsupported', 'unsupported');
    return Promise.resolve(false);
  }

  const currentRequest = probeWithNativeGeolocation()
    .then((state) => {
      if (requestRevision === directRequestRevision) {
        commitSnapshot(state, 'geolocation-probe');
      }
      return state === 'granted';
    })
    .catch((error) => {
      console.log('SLLocation requestLocationPermission error', error);
      if (requestRevision === directRequestRevision) {
        commitSnapshot('indeterminate', 'fallback');
      }
      return false;
    })
    .finally(() => {
      requestLocationPermissionPromise = null;
    });
  requestLocationPermissionPromise = currentRequest;
  return currentRequest;
};

const SLLocation = {
  getLocation,
  defaultLocation,
  initializeAuthorizationStatus,
  // 以下两个 getter 对齐 Native 同步契约：无 Promise、无权限查询、无定位副作用、不改状态。
  locationAuthorizationStatus() {
    return authorizationSnapshot.authorizationStatus;
  },
  locationServicesEnabled() {
    return authorizationSnapshot.servicesEnabled;
  },
  // 仅 Web 桥层/覆盖层使用的诊断接口：返回内部五态，不扩散给根业务。
  locationAuthorizationState(): InternalAuthorizationState {
    return authorizationSnapshot.internalState;
  },
  // 仅重查 Permissions API 保鲜快照（pageDidAppear/visibilitychange 场景）；不触发定位探测。
  refreshAuthorizationStatus,
  subscribeAuthorizationChange(listener: AuthorizationChangeListener) {
    if (typeof listener !== 'function') {
      return () => {};
    }
    authorizationChangeListeners.add(listener);
    addPermissionStatusListener();
    return () => {
      authorizationChangeListeners.delete(listener);
      if (authorizationChangeListeners.size === 0) {
        removePermissionStatusListener();
      }
    };
  },
  // 用户手势触发的原生探测（权限提示组件"申请权限"按钮）。
  // 是合法的权限事实来源，允许更新快照。
  requestLocationPermission,
  refreshHomeLocation() {},
  async startOnceLocation(options, cb) {
    // 单次定位走 Mars/network/cache 链：定位成功与否不写权限快照（见文件头注释）。
    console.log('getOncePosition', options);
    try {
      const location = await getOncePosition();
      const rawLatitude = location?.latitude ?? location?.actualLatitude;
      const rawLongitude = location?.longitude ?? location?.actualLongitude;
      const latitude = Number(rawLatitude);
      const longitude = Number(rawLongitude);
      if (
        rawLatitude === null || rawLatitude === undefined || String(rawLatitude).trim() === ''
        || rawLongitude === null || rawLongitude === undefined || String(rawLongitude).trim() === ''
        || !Number.isFinite(latitude) || latitude < -90 || latitude > 90
        || !Number.isFinite(longitude) || longitude < -180 || longitude > 180
      ) {
        throw new Error('Location coordinates are unavailable');
      }

      const normalizedLocation = { ...location, latitude, longitude };
      const result = {
        ...normalizedLocation,
        location: normalizedLocation,
        errCode: 0,
      };
      try {
        cb?.(result);
      } catch (error) {
        console.error('SLLocation callback error', error);
      }
      return result;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
};

export default SLLocation;
